import { gql } from "@apollo/client";
import { ArrayValue } from "@asmbl/shared/types";
import { createContext, useContext } from "react";
import {
  CondensedTable_compCycle,
  CondensedTable_compCycleBudget,
  CondensedTable_participant,
  CondensedTable_valuation,
  CondensedTable_matrix as Matrix,
  TableDataContext2_position,
} from "src/__generated__/graphql";
import { CondensedTableCurrentDepartmentCell2 } from "../Cells/CondensedTableCurrentDepartmentCell2";
import { CondensedTableCurrentLadderCell2 } from "../Cells/CondensedTableCurrentLadderCell2";
import { CondensedTableCurrentLevelCell2 } from "../Cells/CondensedTableCurrentLevelCell2";
import { CondensedTableNewDepartmentCell2 } from "../Cells/CondensedTableNewDepartmentCell2";
import { CondensedTableNewLadderCell2 } from "../Cells/CondensedTableNewLadderCell2";
import { CondensedTableNewLevelCell2 } from "../Cells/CondensedTableNewLevelCell2";
import { CondensedTableNewPositionCell2 } from "../Cells/CondensedTableNewPositionCell2";
import { CurrencyCodeWithAll } from "../CondensedTable/CondensedTableCurrencyPicker";

/* This context stores values that are 'globally' useful for the Condensed
 * Table, such as valuation and available positions. Providing these values
 * through a context allows us to bridge over the `react-table` rendering,
 * which requires a consistent Column prop interface.
 */
type TableDataContextValue = {
  compCycleId: number;
  availablePositions: TableDataContext2_position[];
  isActiveCycle: boolean;
  selectedCurrency: CurrencyCodeWithAll;
  indirectReportIds: number[];
  currentPhase: Phase | null;
  compCyclePhases: Phase[];
  matrices: Matrix[];
  valuation: CondensedTable_valuation | null;
  budget: CondensedTable_compCycleBudget | null;
  employees: CondensedTable_participant[];
  compCycle: CondensedTable_compCycle | null;
  recommendationsPrefill: boolean;
  isViewOnly: boolean;
};

type Phase = {
  id: number;
  phaseOrder: number;
  startDate: GraphQL_DateTime;
};

export type MatrixPerfRatingOption = ArrayValue<
  Matrix["matrixGuides"]
>["matrixPerfRatingOption"];

export const TableDataContext2 = createContext<TableDataContextValue>({
  compCycleId: -1,
  availablePositions: [],
  isActiveCycle: false,
  selectedCurrency: "all",
  indirectReportIds: [],
  currentPhase: null,
  compCyclePhases: [],
  matrices: [],
  valuation: null,
  budget: null,
  employees: [],
  compCycle: null,
  recommendationsPrefill: false,
  isViewOnly: false,
});

export function useTableData() {
  return useContext(TableDataContext2);
}

export const TableDataContextProvider = Object.assign(
  TableDataContext2.Provider,
  {
    fragments: () => ({
      position: gql`
        ${CondensedTableCurrentDepartmentCell2.fragments.position}
        ${CondensedTableNewDepartmentCell2.fragments.position}
        ${CondensedTableCurrentLadderCell2.fragments.position}
        ${CondensedTableNewLadderCell2.fragments.position}
        ${CondensedTableCurrentLevelCell2.fragments.position}
        ${CondensedTableNewLevelCell2.fragments.position}
        ${CondensedTableNewPositionCell2.fragments.position}
        fragment TableDataContext2_position on Position {
          ...CondensedTableCurrentDepartmentCell_position
          ...CondensedTableNewDepartmentCell_position
          ...CondensedTableCurrentLadderCell_position
          ...CondensedTableNewLadderCell_position
          ...CondensedTableCurrentLevelCell_position
          ...CondensedTableNewLevelCell_position
          ...CondensedTableNewPositionCell2_position
        }
      `,
      user: gql`
        fragment TableDataContext_user on User {
          id
          employee {
            id
            indirectReportIds
          }
        }
      `,
    }),
  }
);
